import { useContext, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flatFacilitatorRoutes } from './pages/facilitator/flow';
import { Context } from '../../context';
import { apiFetch } from './apis/api';

export const ADMIN_PROGRESS_QUERY_KEY = ['adminProgress'];

const MODULE_ROUTE_CONFIG = [
  {
    key: 'introduction',
    pathPrefix: '/facilitator/introduction',
  },
  {
    key: 'strategies1',
    pathPrefix: '/facilitator/strategies-1',
  },
  {
    key: 'strategies2',
    pathPrefix: '/facilitator/strategies-2',
  },
  {
    key: 'hybridEZdelivery',
    pathPrefix: '/facilitator/hybrid-ezparent-delivery',
  },
];

const DEFAULT_ADMIN_PROGRESS = {
  initialVideoWatched: false,
  preSurveyCompleted: false,
  postSurveyCompleted: false,
  postSurveyCompletedAt: null,
  introduction: {
    locked: true,
    currentIndex: 0,
  },
  strategies1: {
    locked: true,
    currentIndex: 0,
  },
  strategies2: {
    locked: true,
    currentIndex: 0,
  },
  hybridEZdelivery: {
    locked: true,
    currentIndex: 0,
  },
};

export const normalizeAdminProgress = (progress = {}) => {
  const normalizedModules = MODULE_ROUTE_CONFIG.reduce((acc, moduleConfig) => {
    const currentModule = progress?.[moduleConfig.key] || {};

    acc[moduleConfig.key] = {
      locked:
        typeof currentModule.locked === 'boolean'
          ? currentModule.locked
          : DEFAULT_ADMIN_PROGRESS[moduleConfig.key].locked,
      currentIndex: Number.isInteger(currentModule.currentIndex)
        ? currentModule.currentIndex
        : DEFAULT_ADMIN_PROGRESS[moduleConfig.key].currentIndex,
    };

    return acc;
  }, {});

  return {
    initialVideoWatched:
      typeof progress?.initialVideoWatched === 'boolean'
        ? progress.initialVideoWatched
        : DEFAULT_ADMIN_PROGRESS.initialVideoWatched,
    preSurveyCompleted:
      typeof progress?.preSurveyCompleted === 'boolean'
        ? progress.preSurveyCompleted
        : DEFAULT_ADMIN_PROGRESS.preSurveyCompleted,
    postSurveyCompleted:
      typeof progress?.postSurveyCompleted === 'boolean'
        ? progress.postSurveyCompleted
        : DEFAULT_ADMIN_PROGRESS.postSurveyCompleted,
    postSurveyCompletedAt:
      typeof progress?.postSurveyCompletedAt === 'string' &&
      progress.postSurveyCompletedAt.trim()
        ? progress.postSurveyCompletedAt
        : DEFAULT_ADMIN_PROGRESS.postSurveyCompletedAt,
    ...normalizedModules,
  };
};

const fetchAdminProgress = async () => {
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/progress/admin`,
    {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const data = await response.json();
  return normalizeAdminProgress(data);
};

const postAdminProgress = async (progress) => {
  const response = await apiFetch(
    `${process.env.REACT_APP_API_URL}/api/progress/admin`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(progress),
    },
  );

  try {
    const data = await response.json();
    return normalizeAdminProgress(data);
  } catch (_error) {
    return normalizeAdminProgress(progress);
  }
};

const postAdminInitialVideo = async () => {
  await apiFetch(`${process.env.REACT_APP_API_URL}/api/progress/initialVideo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return true;
};

const getRoutesForModule = (pathPrefix) =>
  flatFacilitatorRoutes.filter((route) => route.fullUrl.startsWith(pathPrefix));

const getRouteIndexWithinModule = (pathname, pathPrefix) =>
  getRoutesForModule(pathPrefix).findIndex(
    (route) => route.fullUrl === pathname,
  );

const getModuleForPath = (pathname) =>
  MODULE_ROUTE_CONFIG.find((moduleConfig) =>
    pathname.startsWith(moduleConfig.pathPrefix),
  );

const isModuleComplete = (moduleConfig, progress) => {
  const routes = getRoutesForModule(moduleConfig.pathPrefix);
  const moduleProgress = progress?.[moduleConfig.key];
  const moduleIndex = MODULE_ROUTE_CONFIG.findIndex(
    (config) => config.key === moduleConfig.key,
  );
  const isFinalModule = moduleIndex === MODULE_ROUTE_CONFIG.length - 1;

  if (!routes.length || !moduleProgress || moduleProgress.locked) {
    return false;
  }

  if (isFinalModule) {
    return progress?.postSurveyCompleted === true;
  }

  const nextModuleConfig = MODULE_ROUTE_CONFIG[moduleIndex + 1];

  return progress?.[nextModuleConfig.key]?.locked === false;
};

export const isAdminProgramCompleteFromProgress = (progress) => {
  const normalizedProgress = normalizeAdminProgress(progress);
  return normalizedProgress.postSurveyCompleted === true;
};

export const isPathAccessibleForProgress = (pathname, currentProgress) => {
  const moduleConfig = getModuleForPath(pathname);

  if (!moduleConfig) {
    return true;
  }

  const normalizedProgress = normalizeAdminProgress(currentProgress);
  const currentIndex = getRouteIndexWithinModule(
    pathname,
    moduleConfig.pathPrefix,
  );
  const moduleIndex = MODULE_ROUTE_CONFIG.findIndex(
    (config) => config.key === moduleConfig.key,
  );
  const moduleProgress = normalizedProgress[moduleConfig.key];

  if (currentIndex < 0) {
    return false;
  }

  if (moduleProgress?.locked === false) {
    return currentIndex <= moduleProgress.currentIndex;
  }

  if (moduleIndex === 0) {
    return currentIndex === 0;
  }

  const previousModuleConfig = MODULE_ROUTE_CONFIG[moduleIndex - 1];

  return (
    currentIndex === 0 &&
    isModuleComplete(previousModuleConfig, normalizedProgress)
  );
};

export const deriveProgressFromPath = (pathname, currentProgress) => {
  const moduleConfig = getModuleForPath(pathname);

  if (!moduleConfig) {
    return normalizeAdminProgress(currentProgress);
  }

  const currentIndex = getRouteIndexWithinModule(
    pathname,
    moduleConfig.pathPrefix,
  );

  if (currentIndex < 0) {
    return normalizeAdminProgress(currentProgress);
  }

  if (!isPathAccessibleForProgress(pathname, currentProgress)) {
    return normalizeAdminProgress(currentProgress);
  }

  const nextProgress = normalizeAdminProgress(currentProgress);

  nextProgress[moduleConfig.key] = {
    locked: false,
    currentIndex: Math.max(
      nextProgress[moduleConfig.key]?.currentIndex ?? 0,
      currentIndex,
    ),
  };

  return nextProgress;
};

export const deriveSequentialNextProgress = (pathname, currentProgress) => {
  const moduleConfig = getModuleForPath(pathname);

  if (!moduleConfig) {
    return normalizeAdminProgress(currentProgress);
  }

  const currentIndex = getRouteIndexWithinModule(
    pathname,
    moduleConfig.pathPrefix,
  );

  if (currentIndex < 0) {
    return normalizeAdminProgress(currentProgress);
  }

  const nextProgress = normalizeAdminProgress(currentProgress);

  nextProgress[moduleConfig.key] = {
    locked: false,
    currentIndex: Math.max(
      nextProgress[moduleConfig.key]?.currentIndex ?? 0,
      currentIndex,
    ),
  };

  return nextProgress;
};

export const deriveStartedAdminProgress = (currentProgress = {}) => {
  const nextProgress = normalizeAdminProgress(currentProgress);

  nextProgress.introduction = {
    locked: false,
    currentIndex: Math.max(nextProgress.introduction?.currentIndex ?? 0, 0),
  };

  return nextProgress;
};

export const hasProgressPassedPath = (pathname, currentProgress) => {
  const moduleConfig = getModuleForPath(pathname);

  if (!moduleConfig) {
    return false;
  }

  const normalizedProgress = normalizeAdminProgress(currentProgress);
  const currentIndex = getRouteIndexWithinModule(
    pathname,
    moduleConfig.pathPrefix,
  );

  if (currentIndex < 0) {
    return false;
  }

  const moduleProgress = normalizedProgress[moduleConfig.key];

  if (!moduleProgress || moduleProgress.locked) {
    return false;
  }

  if (moduleProgress.currentIndex > currentIndex) {
    return true;
  }

  const routes = getRoutesForModule(moduleConfig.pathPrefix);
  const isLastRouteInModule = currentIndex === routes.length - 1;

  return (
    moduleProgress.currentIndex === currentIndex &&
    isLastRouteInModule &&
    isModuleComplete(moduleConfig, normalizedProgress)
  );
};

export const deriveLastVisitedRouteFromProgress = (progress) => {
  const normalizedProgress = normalizeAdminProgress(progress);

  for (let index = MODULE_ROUTE_CONFIG.length - 1; index >= 0; index -= 1) {
    const moduleConfig = MODULE_ROUTE_CONFIG[index];
    const moduleProgress = normalizedProgress[moduleConfig.key];

    if (moduleProgress.locked) {
      continue;
    }

    const routes = getRoutesForModule(moduleConfig.pathPrefix);
    const safeIndex = Math.min(
      Math.max(moduleProgress.currentIndex, 0),
      Math.max(routes.length - 1, 0),
    );

    return routes[safeIndex]?.fullUrl || routes[0]?.fullUrl;
  }

  return getRoutesForModule(MODULE_ROUTE_CONFIG[0].pathPrefix)[0]?.fullUrl;
};

export const hasStartedProgramFromProgress = (progress) => {
  const normalizedProgress = normalizeAdminProgress(progress);

  return MODULE_ROUTE_CONFIG.some(
    (moduleConfig) => normalizedProgress[moduleConfig.key]?.locked === false,
  );
};

const useAdminProgressMutationBase = () => {
  const context = useContext(Context);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postAdminProgress,
    onMutate: async (nextProgress) => {
      await queryClient.cancelQueries({
        queryKey: ADMIN_PROGRESS_QUERY_KEY,
      });

      const normalizedProgress = normalizeAdminProgress(nextProgress);
      const previousProgress = queryClient.getQueryData(
        ADMIN_PROGRESS_QUERY_KEY,
      );

      queryClient.setQueryData(ADMIN_PROGRESS_QUERY_KEY, normalizedProgress);

      return { previousProgress };
    },
    onError: (error, _variables, mutationContext) => {
      if (error?.status === 401) {
        context.logout();
      }

      if (mutationContext?.previousProgress) {
        queryClient.setQueryData(
          ADMIN_PROGRESS_QUERY_KEY,
          normalizeAdminProgress(mutationContext.previousProgress),
        );
      }
    },
    onSuccess: (savedProgress) => {
      const normalizedProgress = normalizeAdminProgress(savedProgress);
      queryClient.setQueryData(ADMIN_PROGRESS_QUERY_KEY, normalizedProgress);
    },
  });
};

export const useAdminProgressQuery = ({ currentPath }) => {
  const context = useContext(Context);
  const hasToken = Boolean(localStorage.getItem('token'));
  const query = useQuery({
    queryKey: ADMIN_PROGRESS_QUERY_KEY,
    queryFn: fetchAdminProgress,
    enabled: hasToken,
    retry: false,
  });

  useEffect(() => {
    if (query.error?.status === 401) {
      context.logout();
    }
  }, [context, query.error]);

  const normalizedProgress = useMemo(
    () => normalizeAdminProgress(query.data || DEFAULT_ADMIN_PROGRESS),
    [query.data],
  );

  return {
    adminProgress: normalizedProgress,
    hasLoadedAdminProgress: query.isSuccess,
    isLoadingAdminProgress: query.isLoading,
    isSavingAdminProgress: false,
    isUsingMockAdminProgress: false,
  };
};

export const useAdminProgressMutation = useAdminProgressMutationBase;

export const useStartAdminProgressMutation = useAdminProgressMutationBase;

export const useAdminInitialVideoMutation = () => {
  const context = useContext(Context);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postAdminInitialVideo,
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: ADMIN_PROGRESS_QUERY_KEY,
      });

      const previousProgress = queryClient.getQueryData(
        ADMIN_PROGRESS_QUERY_KEY,
      );
      const normalizedPreviousProgress =
        normalizeAdminProgress(previousProgress);

      queryClient.setQueryData(ADMIN_PROGRESS_QUERY_KEY, {
        ...normalizedPreviousProgress,
        initialVideoWatched: true,
      });

      return { previousProgress };
    },
    onError: (error, _variables, mutationContext) => {
      if (error?.status === 401) {
        context.logout();
      }

      if (mutationContext?.previousProgress) {
        queryClient.setQueryData(
          ADMIN_PROGRESS_QUERY_KEY,
          normalizeAdminProgress(mutationContext.previousProgress),
        );
      }
    },
    onSuccess: () => {
      const currentProgress = normalizeAdminProgress(
        queryClient.getQueryData(ADMIN_PROGRESS_QUERY_KEY),
      );

      queryClient.setQueryData(ADMIN_PROGRESS_QUERY_KEY, {
        ...currentProgress,
        initialVideoWatched: true,
      });
    },
  });
};
